import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, 
  Mail, 
  Server,
  Settings, 
  CheckCircle, 
  Eye,
  EyeOff,
  ExternalLink,
  Loader2,
  AlertCircle,
  TestTube,
  Info,
  Inbox,
  RefreshCw
} from 'lucide-react';

const EmailSettings = () => {
  const { token, getAuthHeaders, user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingImap, setTestingImap] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showImapPassword, setShowImapPassword] = useState(false);
  const [smtpPresets, setSmtpPresets] = useState({});
  const [imapPresets, setImapPresets] = useState({});
  const [detectedMailboxes, setDetectedMailboxes] = useState([]);
  
  const [settings, setSettings] = useState({
    email_method: 'platform',
    business_display_name: '',
    reply_to_email: '',
    smtp_server: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
    smtp_use_tls: true,
    smtp_use_ssl: false,
    smtp_configured: false,
    smtp_password_set: false,
    // IMAP settings
    imap_enabled: false,
    imap_server: '',
    imap_port: 993,
    imap_username: '',
    imap_password: '',
    imap_use_ssl: true,
    imap_mailboxes: ['INBOX'],
    imap_fetch_frequency: 5,
    imap_configured: false,
    imap_password_set: false
  });

  // Global IMAP settings (Super Admin only)
  const [globalImapSettings, setGlobalImapSettings] = useState({
    imap_enabled: false,
    imap_server: '',
    imap_port: 993,
    imap_username: '',
    imap_password: '',
    imap_use_ssl: true,
    imap_mailboxes: ['INBOX'],
    imap_fetch_frequency: 5,
    imap_configured: false,
    imap_password_set: false
  });
  const [savingGlobalImap, setSavingGlobalImap] = useState(false);
  const [testingGlobalImap, setTestingGlobalImap] = useState(false);
  const [globalImapMailboxes, setGlobalImapMailboxes] = useState([]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [settingsRes, presetsRes, calendarsRes, imapPresetsRes] = await Promise.all([
        fetch(`${API_URL}/api/email-settings/`, {
          headers: getAuthHeaders()
        }),
        fetch(`${API_URL}/api/email-settings/smtp-presets`, {
          headers: getAuthHeaders()
        }),
        fetch(`${API_URL}/api/calendars`, {
          headers: getAuthHeaders()
        }),
        fetch(`${API_URL}/api/email-settings/imap-presets`, {
          headers: getAuthHeaders()
        })
      ]);

      let defaultBusinessName = '';
      
      // Get calendar name for default business name
      if (calendarsRes.ok) {
        const calendarsData = await calendarsRes.json();
        if (calendarsData.data && calendarsData.data.length > 0) {
          defaultBusinessName = calendarsData.data[0].name || '';
        }
      }

      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setSettings(prev => ({
          ...prev,
          ...data,
          // Default business_display_name to calendar name if not set
          business_display_name: data.business_display_name || defaultBusinessName,
          smtp_password: '', // Don't show actual password
          imap_password: ''  // Don't show actual password
        }));
      } else {
        // If no settings exist yet, use default calendar name
        setSettings(prev => ({
          ...prev,
          business_display_name: defaultBusinessName
        }));
      }

      if (presetsRes.ok) {
        const presets = await presetsRes.json();
        setSmtpPresets(presets);
      }

      if (imapPresetsRes.ok) {
        const presets = await imapPresetsRes.json();
        setImapPresets(presets);
      }

      // Fetch global IMAP settings if Super Admin
      if (isSuperAdmin()) {
        try {
          const globalImapRes = await fetch(`${API_URL}/api/email-settings/global-imap`, {
            headers: getAuthHeaders()
          });
          if (globalImapRes.ok) {
            const globalData = await globalImapRes.json();
            if (globalData.success) {
              setGlobalImapSettings(prev => ({
                ...prev,
                ...globalData.data,
                imap_password: '' // Don't show actual password
              }));
            }
          }
        } catch (err) {
          console.error('Error fetching global IMAP settings:', err);
        }
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applyPreset = (presetKey) => {
    const preset = smtpPresets[presetKey];
    if (preset) {
      setSettings(prev => ({
        ...prev,
        smtp_server: preset.smtp_server,
        smtp_port: preset.smtp_port,
        smtp_use_tls: preset.smtp_use_tls,
        smtp_use_ssl: preset.smtp_use_ssl
      }));
    }
  };

  const applyImapPreset = (presetKey, isGlobal = false) => {
    const preset = imapPresets[presetKey];
    if (preset) {
      if (isGlobal) {
        setGlobalImapSettings(prev => ({
          ...prev,
          imap_server: preset.imap_server,
          imap_port: preset.imap_port,
          imap_use_ssl: preset.imap_use_ssl
        }));
      } else {
        setSettings(prev => ({
          ...prev,
          imap_server: preset.imap_server,
          imap_port: preset.imap_port,
          imap_use_ssl: preset.imap_use_ssl
        }));
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      const payload = {
        email_method: settings.email_method,
        business_display_name: settings.business_display_name,
        reply_to_email: settings.reply_to_email
      };

      // Include SMTP settings if using SMTP
      if (settings.email_method === 'smtp') {
        payload.smtp_server = settings.smtp_server;
        payload.smtp_port = settings.smtp_port;
        payload.smtp_username = settings.smtp_username;
        payload.smtp_from_email = settings.smtp_from_email;
        payload.smtp_from_name = settings.smtp_from_name;
        payload.smtp_use_tls = settings.smtp_use_tls;
        payload.smtp_use_ssl = settings.smtp_use_ssl;
        
        // Only include password if user entered a new one
        if (settings.smtp_password) {
          payload.smtp_password = settings.smtp_password;
        }
      }

      // Include IMAP settings (for custom branding)
      if (settings.imap_enabled) {
        payload.imap_enabled = settings.imap_enabled;
        payload.imap_server = settings.imap_server;
        payload.imap_port = settings.imap_port;
        payload.imap_username = settings.imap_username;
        payload.imap_use_ssl = settings.imap_use_ssl;
        payload.imap_mailboxes = settings.imap_mailboxes;
        payload.imap_fetch_frequency = settings.imap_fetch_frequency;
        
        if (settings.imap_password) {
          payload.imap_password = settings.imap_password;
        }
      }

      const response = await fetch(`${API_URL}/api/email-settings/`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to save settings');
      }

      setSuccess('Email settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
      
      // Refresh data
      fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setError('');

    try {
      // First save the settings
      await handleSave();

      const response = await fetch(`${API_URL}/api/email-settings/test-smtp`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders()
        }
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(data.message);
      } else {
        setError(data.message);
      }

      setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setTesting(false);
    }
  };

  const handleTestImap = async () => {
    setTestingImap(true);
    setError('');

    try {
      // First save the settings
      await handleSave();

      const response = await fetch(`${API_URL}/api/email-settings/test-imap`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(data.message);
        if (data.mailboxes) {
          setDetectedMailboxes(data.mailboxes);
        }
      } else {
        setError(data.message);
      }

      setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setTestingImap(false);
    }
  };

  // Global IMAP handlers (Super Admin only)
  const handleSaveGlobalImap = async () => {
    setSavingGlobalImap(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/email-settings/global-imap`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(globalImapSettings)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to save global IMAP settings');
      }

      setSuccess('Global IMAP settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
      fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingGlobalImap(false);
    }
  };

  const handleTestGlobalImap = async () => {
    setTestingGlobalImap(true);
    setError('');

    try {
      // First save the settings
      await handleSaveGlobalImap();

      const response = await fetch(`${API_URL}/api/email-settings/test-global-imap`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(data.message);
        if (data.mailboxes) {
          setGlobalImapMailboxes(data.mailboxes);
        }
      } else {
        setError(data.message);
      }

      setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setTestingGlobalImap(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex justify-center items-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <section className="py-20 bg-gradient-to-br from-white to-amber-50 min-h-screen">
        <div className="max-w-3xl mx-auto px-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-800"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Email Settings</h1>
            <p className="text-gray-600">Configure how your business sends emails to customers</p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded flex items-center gap-2">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              {success}
            </div>
          )}

          {/* Email Method Selection */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Email Sending Method</h2>
            
            <div className="space-y-4">
              <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="email_method"
                  value="platform"
                  checked={settings.email_method === 'platform'}
                  onChange={(e) => handleChange('email_method', e.target.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">Use Platform Email (Recommended)</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Emails are sent from <strong>notifications@yibooking.com</strong>. 
                    Customer replies go to your reply-to email address.
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="email_method"
                  value="smtp"
                  checked={settings.email_method === 'smtp'}
                  onChange={(e) => handleChange('email_method', e.target.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Server className="w-5 h-5 text-green-600" />
                    <span className="font-medium">Use My Own Email (SMTP)</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Emails are sent from your own email address (e.g., Gmail, Outlook, Yahoo).
                    Full control over your email branding.
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Platform Email Settings */}
          {settings.email_method === 'platform' && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Platform Email Settings</h2>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">How it works:</p>
                    <p className="mt-1">
                      Emails will be sent as: <strong>"{settings.business_display_name || 'Your Business'} via YiBooking" &lt;notifications@yibooking.com&gt;</strong>
                    </p>
                    <p className="mt-1">
                      When customers reply, their email goes to: <strong>{settings.reply_to_email || user?.email || 'your email'}</strong>
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Display Name
                  </label>
                  <input
                    type="text"
                    value={settings.business_display_name}
                    onChange={(e) => handleChange('business_display_name', e.target.value)}
                    placeholder="e.g., Salon ABC"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    data-testid="business-name-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This appears in the email "From" field
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reply-To Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={settings.reply_to_email}
                    onChange={(e) => handleChange('reply_to_email', e.target.value)}
                    placeholder="your.email@gmail.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    data-testid="reply-to-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Customer replies will be sent to this email address
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* SMTP Email Settings */}
          {settings.email_method === 'smtp' && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">SMTP Server Settings</h2>
              
              {/* Quick Presets */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quick Setup - Select Your Email Provider
                </label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(smtpPresets).map(([key, preset]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => applyPreset(key)}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
                    >
                      {preset.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SMTP Server <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={settings.smtp_server}
                    onChange={(e) => handleChange('smtp_server', e.target.value)}
                    placeholder="smtp.gmail.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    data-testid="smtp-server-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Port <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={settings.smtp_port}
                    onChange={(e) => handleChange('smtp_port', parseInt(e.target.value))}
                    placeholder="587"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    data-testid="smtp-port-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email/Username <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={settings.smtp_username}
                    onChange={(e) => handleChange('smtp_username', e.target.value)}
                    placeholder="your.email@gmail.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    data-testid="smtp-username-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password/App Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={settings.smtp_password}
                      onChange={(e) => handleChange('smtp_password', e.target.value)}
                      placeholder={settings.smtp_password_set ? '••••••••' : 'Enter password'}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent pr-10"
                      data-testid="smtp-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {settings.smtp_password_set && !settings.smtp_password && (
                    <p className="text-xs text-gray-500 mt-1">Leave blank to keep current password</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={settings.smtp_from_email}
                    onChange={(e) => handleChange('smtp_from_email', e.target.value)}
                    placeholder="your.email@gmail.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    data-testid="smtp-from-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From Name
                  </label>
                  <input
                    type="text"
                    value={settings.smtp_from_name}
                    onChange={(e) => handleChange('smtp_from_name', e.target.value)}
                    placeholder="Your Business Name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    data-testid="smtp-from-name-input"
                  />
                </div>
              </div>

              <div className="flex gap-4 mt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.smtp_use_tls}
                    onChange={(e) => handleChange('smtp_use_tls', e.target.checked)}
                    className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                  />
                  <span className="text-sm text-gray-700">Use TLS</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.smtp_use_ssl}
                    onChange={(e) => handleChange('smtp_use_ssl', e.target.checked)}
                    className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                  />
                  <span className="text-sm text-gray-700">Use SSL</span>
                </label>
              </div>

              {/* Help links */}
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 font-medium mb-2">Need help setting up?</p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://support.google.com/accounts/answer/185833"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    Gmail App Password <ExternalLink className="w-3 h-3" />
                  </a>
                  <a
                    href="https://support.microsoft.com/en-us/account-billing/using-app-passwords"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    Outlook App Password <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Global IMAP Settings - Super Admin Only */}
          {isSuperAdmin() && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-6 border-2 border-purple-200">
              <div className="flex items-center gap-2 mb-4">
                <Inbox className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-semibold text-gray-800">Global Inbound Email (IMAP)</h2>
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">Super Admin</span>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Configure system-wide IMAP settings to receive customer email replies. Calendar Admins can override with their own settings for custom branding.
              </p>

              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  id="global_imap_enabled"
                  checked={globalImapSettings.imap_enabled}
                  onChange={(e) => setGlobalImapSettings(prev => ({ ...prev, imap_enabled: e.target.checked }))}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <label htmlFor="global_imap_enabled" className="text-sm font-medium text-gray-700">
                  Enable Global Inbound Email
                </label>
              </div>

              {globalImapSettings.imap_enabled && (
                <>
                  {/* IMAP Presets */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quick Setup - Select Email Provider
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(imapPresets).map(([key, preset]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => applyImapPreset(key, true)}
                          className="px-3 py-1 bg-purple-100 hover:bg-purple-200 rounded text-sm font-medium transition-colors text-purple-700"
                        >
                          {preset.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">IMAP Server *</label>
                      <input
                        type="text"
                        value={globalImapSettings.imap_server}
                        onChange={(e) => setGlobalImapSettings(prev => ({ ...prev, imap_server: e.target.value }))}
                        placeholder="imap.hostinger.com"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Port *</label>
                      <input
                        type="number"
                        value={globalImapSettings.imap_port}
                        onChange={(e) => setGlobalImapSettings(prev => ({ ...prev, imap_port: parseInt(e.target.value) }))}
                        placeholder="993"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email/Username *</label>
                      <input
                        type="email"
                        value={globalImapSettings.imap_username}
                        onChange={(e) => setGlobalImapSettings(prev => ({ ...prev, imap_username: e.target.value }))}
                        placeholder="support@yibooking.com"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                      <div className="relative">
                        <input
                          type={showImapPassword ? 'text' : 'password'}
                          value={globalImapSettings.imap_password}
                          onChange={(e) => setGlobalImapSettings(prev => ({ ...prev, imap_password: e.target.value }))}
                          placeholder={globalImapSettings.imap_password_set ? '••••••••' : 'Enter password'}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowImapPassword(!showImapPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showImapPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fetch Frequency</label>
                      <select
                        value={globalImapSettings.imap_fetch_frequency}
                        onChange={(e) => setGlobalImapSettings(prev => ({ ...prev, imap_fetch_frequency: parseInt(e.target.value) }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value={1}>Every 1 minute</option>
                        <option value={2}>Every 2 minutes</option>
                        <option value={5}>Every 5 minutes</option>
                        <option value={10}>Every 10 minutes</option>
                        <option value={15}>Every 15 minutes</option>
                        <option value={30}>Every 30 minutes</option>
                      </select>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="global_imap_ssl"
                        checked={globalImapSettings.imap_use_ssl}
                        onChange={(e) => setGlobalImapSettings(prev => ({ ...prev, imap_use_ssl: e.target.checked }))}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <label htmlFor="global_imap_ssl" className="ml-2 text-sm text-gray-700">Use SSL (recommended)</label>
                    </div>
                  </div>

                  {/* Mailboxes */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mailboxes to Monitor</label>
                    <input
                      type="text"
                      value={globalImapSettings.imap_mailboxes?.join(', ')}
                      onChange={(e) => setGlobalImapSettings(prev => ({ 
                        ...prev, 
                        imap_mailboxes: e.target.value.split(',').map(m => m.trim()).filter(m => m)
                      }))}
                      placeholder="INBOX, Support"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Comma-separated list of mailbox names</p>
                    {globalImapMailboxes.length > 0 && (
                      <div className="mt-2 text-xs text-purple-600">
                        Detected mailboxes: {globalImapMailboxes.slice(0, 10).join(', ')}
                      </div>
                    )}
                  </div>

                  {/* Global IMAP Buttons */}
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={handleSaveGlobalImap}
                      disabled={savingGlobalImap}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {savingGlobalImap ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings className="w-4 h-4" />}
                      Save Global IMAP
                    </button>
                    <button
                      onClick={handleTestGlobalImap}
                      disabled={testingGlobalImap || !globalImapSettings.imap_server}
                      className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {testingGlobalImap ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                      Test Connection
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Calendar Admin IMAP Settings (Custom Branding Override) */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Inbox className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-800">Inbound Email (IMAP)</h2>
              {!isSuperAdmin() && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Custom Branding</span>
              )}
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Configure IMAP to receive customer email replies into your Message Console.
              {!isSuperAdmin() && ' This overrides the global system settings for your account.'}
            </p>

            <div className="flex items-center gap-2 mb-4">
              <input
                type="checkbox"
                id="imap_enabled"
                checked={settings.imap_enabled}
                onChange={(e) => handleChange('imap_enabled', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="imap_enabled" className="text-sm font-medium text-gray-700">
                Enable Custom Inbound Email
              </label>
            </div>

            {settings.imap_enabled && (
              <>
                {/* IMAP Presets */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quick Setup - Select Email Provider
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(imapPresets).map(([key, preset]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => applyImapPreset(key, false)}
                        className="px-3 py-1 bg-blue-100 hover:bg-blue-200 rounded text-sm font-medium transition-colors text-blue-700"
                      >
                        {preset.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">IMAP Server *</label>
                    <input
                      type="text"
                      value={settings.imap_server}
                      onChange={(e) => handleChange('imap_server', e.target.value)}
                      placeholder="imap.hostinger.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Port *</label>
                    <input
                      type="number"
                      value={settings.imap_port}
                      onChange={(e) => handleChange('imap_port', parseInt(e.target.value))}
                      placeholder="993"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email/Username *</label>
                    <input
                      type="email"
                      value={settings.imap_username}
                      onChange={(e) => handleChange('imap_username', e.target.value)}
                      placeholder="your.email@domain.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                    <div className="relative">
                      <input
                        type={showImapPassword ? 'text' : 'password'}
                        value={settings.imap_password}
                        onChange={(e) => handleChange('imap_password', e.target.value)}
                        placeholder={settings.imap_password_set ? '••••••••' : 'Enter password'}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowImapPassword(!showImapPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showImapPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fetch Frequency</label>
                    <select
                      value={settings.imap_fetch_frequency}
                      onChange={(e) => handleChange('imap_fetch_frequency', parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value={1}>Every 1 minute</option>
                      <option value={2}>Every 2 minutes</option>
                      <option value={5}>Every 5 minutes</option>
                      <option value={10}>Every 10 minutes</option>
                      <option value={15}>Every 15 minutes</option>
                      <option value={30}>Every 30 minutes</option>
                    </select>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="imap_ssl"
                      checked={settings.imap_use_ssl}
                      onChange={(e) => handleChange('imap_use_ssl', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="imap_ssl" className="ml-2 text-sm text-gray-700">Use SSL (recommended)</label>
                  </div>
                </div>

                {/* Mailboxes */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mailboxes to Monitor</label>
                  <input
                    type="text"
                    value={settings.imap_mailboxes?.join(', ')}
                    onChange={(e) => handleChange('imap_mailboxes', e.target.value.split(',').map(m => m.trim()).filter(m => m))}
                    placeholder="INBOX, Support"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Comma-separated list of mailbox names</p>
                  {detectedMailboxes.length > 0 && (
                    <div className="mt-2 text-xs text-blue-600">
                      Detected mailboxes: {detectedMailboxes.slice(0, 10).join(', ')}
                    </div>
                  )}
                </div>

                {/* Test IMAP Button */}
                <div className="mt-4">
                  <button
                    onClick={handleTestImap}
                    disabled={testingImap || !settings.imap_server}
                    className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {testingImap ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                    Test IMAP Connection
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="save-btn"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Settings className="w-5 h-5" />
              )}
              Save Settings
            </button>

            {settings.email_method === 'smtp' && (
              <button
                onClick={handleTestConnection}
                disabled={testing || !settings.smtp_server}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="test-btn"
              >
                {testing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <TestTube className="w-5 h-5" />
                )}
                Test Connection
              </button>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default EmailSettings;
